import * as React from 'react';

import { ContentSection, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { SalesDetailLayout } from '../../_layouts/SalesDetailLayout';

import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import {
  $ticketDetailsFormById,
  $updateTicketDetailsField,
  DEFAULT_DETAILS_FORM,
  type TicketDetailsFormState,
} from '~/modules/trade/_state/scaleTicketInboundInfoForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { PurchaseLoadRecord } from '~/modules/trade/types/purchaseLoad';
import { FieldFocusIndicator } from '~/shared/components/FieldFocusIndicator';

type TicketFormFieldsProps = {
  ticket: PurchaseLoadRecord;
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ TICKET INFO FORM ⚛️ ⚛️ ⚛️
//
export const TicketInfoForm = ({
  ticket,
  disabled = false,
}: TicketFormFieldsProps) => {
  const t = useTranslations();
  const ff = t.scaleTicketPro.formFields;

  const setFocusedField = useSetAtom($focusedField);
  const focusPositions = useAtomValue($remoteFocusPositions);

  const form =
    useAtomValue($ticketDetailsFormById).get(ticket.id) ?? DEFAULT_DETAILS_FORM;
  const updateField = useSetAtom($updateTicketDetailsField);

  // Callbacks
  const handleFieldChange = React.useCallback(
    (field: keyof TicketDetailsFormState) => (value: string) => {
      updateField({ field, value, ticketId: ticket.id });
    },
    [updateField, ticket.id],
  );

  return (
    <SalesDetailLayout className="@TicketInfoForm p-0">
      <ContentSection
        title={ff.generalInfo}
        titleClassName="font-bold text-neutral"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldFocusIndicator
            fieldId="receivingTicketNumber"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.receivingTicketNumber}
              value={form.receivingTicketNumber ?? ''}
              onChange={handleFieldChange('receivingTicketNumber')}
              onFocus={() => setFocusedField('receivingTicketNumber')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.receivingTicketNumberPlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="description"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.description}
              value={form.description ?? ''}
              onChange={handleFieldChange('description')}
              onFocus={() => setFocusedField('description')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.descriptionPlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>
        </div>
      </ContentSection>

      <ContentSection
        title={ff.transportDetails}
        titleClassName="font-bold text-neutral"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldFocusIndicator
            fieldId="truckNumber"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.truckNumber}
              value={form.truckNumber ?? ''}
              onChange={handleFieldChange('truckNumber')}
              onFocus={() => setFocusedField('truckNumber')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.truckNumberPlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="trailerNumber"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.trailerNumber}
              value={form.trailerNumber ?? ''}
              onChange={handleFieldChange('trailerNumber')}
              onFocus={() => setFocusedField('trailerNumber')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.trailerNumberPlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="driverName"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.driverName}
              value={form.driverName ?? ''}
              onChange={handleFieldChange('driverName')}
              onFocus={() => setFocusedField('driverName')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.driverNamePlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="containerNumber"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.containerNumber}
              value={form.containerNumber ?? ''}
              onChange={handleFieldChange('containerNumber')}
              onFocus={() => setFocusedField('containerNumber')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.containerNumberPlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="sealNumber"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.sealNumber}
              value={form.sealNumber ?? ''}
              onChange={handleFieldChange('sealNumber')}
              onFocus={() => setFocusedField('sealNumber')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.sealNumberPlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="bolNumber"
            focusPositions={focusPositions}
          >
            <TextField
              label={ff.bolNumber}
              value={form.bolNumber ?? ''}
              onChange={handleFieldChange('bolNumber')}
              onFocus={() => setFocusedField('bolNumber')}
              onBlur={() => setFocusedField(null)}
              placeholder={ff.bolNumberPlaceholder}
              isDisabled={disabled}
            />
          </FieldFocusIndicator>
        </div>
      </ContentSection>
    </SalesDetailLayout>
  );
};
